import { ExportType } from 'constants/exportTypes';
import { Resources } from 'constants/resources';

import { useCallback, useState } from 'react';

import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRecordModalById } from 'hooks/useRecordModalById';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord, RecordContextProvider, useListContext } from 'react-admin';
import { DefaultValues, FieldValues, FormProvider } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { ContextMenu } from 'shared/mui/ContextMenu';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterIdButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterReferenceButton } from 'shared/mui/Filter/FilterButtons/FilterReferenceButton/FilterReferenceButton';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterIdChips } from 'shared/mui/Filter/FilterId/FilterIdChips/FilterIdChips';
import { FilterIdDialog } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialog';
import { FilterIdDialogNotFoundIdAlert } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialogNotFoundIdAlert/FilterIdDialogNotFoundIdAlert';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { FilterIdsValues, FilterIdTypes, useIdFilters } from 'shared/mui/Filter/hooks/useIdFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import {
  AdjustmentAmountField,
  AdjustmentChangeStatusField,
  ChipField,
  CopyToClipboardLinkField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  TextField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { CopyToClipboardField } from 'shared/react-admin/Fields/CopyToClipboardField/CopyToClipboardField';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { GatewayShowDialog } from 'shared/widgets/GatewayShowDialog/GatewayShowDialog';
import { OrderAdjustmentsReferenceDialog } from 'shared/widgets/OrderAdjustmentsReferenceDialog/OrderAdjustmentsReferenceDialog';
import { Gateway } from 'types/embedded/gateway';
import { OrderAdjustments } from 'types/embedded/orderAdjustments';
import { User } from 'types/embedded/user';
import { cleanEmpty } from 'utils/cleanEmpty';
import { getId } from 'utils/getId';

import { ContextMenuCommentButton } from '../components/ContextMenuCommentButton';
import { ContextMenuLinksButton } from '../components/ContextMenuLinksButton';
import { OrderAdjustmentsReportCommentDialog } from '../OrderAdjustmentsReportCommentDialog/OrderAdjustmentsReportCommentDialog';
import { OrderAdjustmentsReportConfirmSearchDialog } from '../OrderAdjustmentsReportConfirmSearchDialog/OrderAdjustmentsReportConfirmSearchDialog';
import { OrderAdjustmentsReportExportDialog } from '../OrderAdjustmentsReportExportDialog/OrderAdjustmentsReportExportDialog';
import { OrderAdjustmentsReportGatewayField } from '../OrderAdjustmentsReportGatewayField/OrderAdjustmentsReportGatewayField';
import { DatagridStyled } from '../OrderAdjustmentsReportList.styled';
import {
  OrderAdjustmentsReportListFilter,
  OrderAdjustmentsReportListFilterFormValues,
} from '../OrderAdjustmentsReportListFilter/OrderAdjustmentsReportListFilter';
import { OrderAdjustmentsReportListFilterChips } from '../OrderAdjustmentsReportListFilterChips/OrderAdjustmentsReportListFilterChips';
import { OrderAdjustmentsReportStatusField } from '../OrderAdjustmentsReportStatusField/OrderAdjustmentsReportStatusField';
import { getRowStyle } from '../utils/getRowStyle';
import { mapFormValuesToExportFilters } from '../utils/mapFormValuesToExportFilters';
import { mapFormValuesToUrlFilters } from '../utils/mapFormValuesToUrlFilters';
import { mapUrlFiltersToFormValues } from '../utils/mapUrlFiltersToFormValues';

const DEFAULT_FORM_VALUES: OrderAdjustmentsReportListFilterFormValues = {
  appliedDate: [null, null],
  adjustmentType: [],
  adjustmentStatus: [],
  impactToBalance: null,
  requestId: null,
  user: [],
};

const DEFAULT_FORM_ID_VALUES: FilterIdsValues = {
  merchantReference: [],
  id: [],
  providerReferenceId: [],
};

type Props = {
  isOpenNotifyDialog: boolean;
  exportType: ExportType;
  onCloseNotifyDialog: VoidFunction;
};

export const OrderAdjustmentsReportListContent: React.FC<Props> = ({
  exportType,
  isOpenNotifyDialog,
  onCloseNotifyDialog,
}) => {
  const translate = useTypedTranslate();

  const { filterValues, data, isFetching } = useListContext();
  const { createPathWithQuery } = useCreatePathWithQuery();

  const [currentIdFilter, setCurrentIdFilter] = useState<FilterIdTypes>(FilterIdTypes.Id);
  const [isOpenIdDialog, setIsOpenIdDialog] = useState<boolean>(false);
  const [
    isOpenOrderAdjustmentsReportReferenceDialog,
    setIsOpenOrderAdjustmentsReportReferenceDialog,
  ] = useState<boolean>(false);

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<OrderAdjustmentsReportListFilterFormValues>({
      mode: 'all',
      defaultValues: mapUrlFiltersToFormValues(filterValues),
      resetValues: DEFAULT_FORM_VALUES,
    });

  const {
    setAppliedFilter: setAppliedIdFilter,
    onSubmit: onSubmitIds,
    onReset: onResetIds,
    appliedIdFilters,
    form: formId,
  } = useIdFilters({
    defaultValues: {
      merchantReference: filterValues?.merchantReference || [],
      id: filterValues?.order || [],
      providerReferenceId: filterValues?.providerReferenceId || [],
    },
    resetValues: DEFAULT_FORM_ID_VALUES,
  });

  const getListFilters = useCallback(
    (values: FieldValues | DefaultValues<FieldValues>) => mapFormValuesToUrlFilters(values),
    [],
  );

  useSyncFilter({
    appliedFilters,
    appliedIdFilters,
    transform: getListFilters,
    transformId: (appliedIdFilters) => {
      return cleanEmpty({
        order: appliedIdFilters?.id,
        'order.merchantReference': appliedIdFilters?.merchantReference,
        'order.providerReferenceId': appliedIdFilters?.providerReferenceId,
      });
    },
  });

  const isListFilterExist = Object.keys(filterValues).length > 0;

  const commentDialog = useRecordModal<OrderAdjustments>();
  const confirmSearchModal = useRecordModal<OrderAdjustments>();
  const gatewayModal = useRecordModalById<Gateway>({ resource: Resources.Gateways });

  const onLinkClick = (record: OrderAdjustments) => {
    onReset();
    onResetIds(currentIdFilter);

    const orderId = getId(record.order?.['@id']);
    formId.setValue('id', [orderId]);
    setAppliedIdFilter('id', [orderId]);
  };

  const notFoundIds = appliedIdFilters.id?.filter(
    (id) => id && !data?.some((orderAdjustment) => getId(orderAdjustment.order?.['@id']) === id),
  );

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterIdButton
            key="filter-by-id"
            onClick={() => {
              setIsOpenIdDialog(true);
            }}
          />,
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton
            disabled={!data?.length || !isListFilterExist}
            key="select-column"
          />,
          <FilterReferenceButton
            key="info"
            onClick={() => setIsOpenOrderAdjustmentsReportReferenceDialog(true)}
          />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <OrderAdjustmentsReportListFilter />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton
                label={translate('app.orderAdjustmentsReport.reset')}
                onReset={onReset}
              />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} appliedIdFilters={appliedIdFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <OrderAdjustmentsReportListFilterChips />
          </FormProvider>
        </FilterContextProvider>
        <FilterContextProvider
          appliedFilters={appliedIdFilters}
          setAppliedFilter={setAppliedIdFilter}
        >
          <FormProvider {...formId}>
            <FilterIdChips
              omit={[FilterIdTypes.Any]}
              onClick={(type) => {
                setIsOpenIdDialog(true);
                setCurrentIdFilter(type);
              }}
            />
            <FilterClearButton
              key={`clear-${currentIdFilter}`}
              onClick={() => {
                onReset();
                onResetIds(currentIdFilter);
              }}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      <FormProvider {...formId}>
        <FilterIdDialog
          currentIdFilter={currentIdFilter}
          notFoundIds={notFoundIds}
          notFoundMerchantIds={[]}
          notFoundProviderIds={[]}
          omit={[FilterIdTypes.Any]}
          onChange={(currentFilter) => setCurrentIdFilter(currentFilter)}
          onClose={() => setIsOpenIdDialog(false)}
          onReset={onResetIds}
          onSubmit={onSubmitIds}
          open={isOpenIdDialog}
        />
      </FormProvider>
      {!isListFilterExist && (
        <Alert
          severity="info"
          sx={{ marginBottom: '10px' }}
          title={translate('app.orderAdjustmentsReport.infoAlertTitle')}
        >
          {translate('app.orderAdjustmentsReport.infoAlertPart1')}{' '}
          <strong>{translate('app.orderAdjustmentsReport.filter')}</strong>{' '}
          {translate('app.orderAdjustmentsReport.infoAlertPart2')}{' '}
          <strong>{translate('app.orderAdjustmentsReport.searchById')}</strong>
          {'. '}
          {translate('app.orderAdjustmentsReport.infoAlertPart3')}
        </Alert>
      )}
      <FilterIdDialogNotFoundIdAlert ids={notFoundIds} isFetching={!!isFetching} />
      {isOpenOrderAdjustmentsReportReferenceDialog && (
        <OrderAdjustmentsReferenceDialog
          onClose={() => setIsOpenOrderAdjustmentsReportReferenceDialog(false)}
          open={isOpenOrderAdjustmentsReportReferenceDialog}
        />
      )}
      {isFetching && isListFilterExist && <DatagridSkeleton columnsCount={15} />}
      {!isFetching && isListFilterExist && (
        <>
          <DatagridStickyContainer>
            <DatagridStyled bulkActionButtons={false} omit={['originId']} rowSx={getRowStyle}>
              <TextField label="ID" sortBy="id" source="originId" />
              <TextField
                label={translate('app.orderAdjustmentsReport.adjustmentType')}
                source="adjustmentType.description"
              />
              <FunctionField
                label={translate('app.orderAdjustmentsReport.clientBalanceChange')}
                render={(record: OrderAdjustments) =>
                  record.impactToBalance
                    ? translate('app.orderAdjustmentsReport.hasBalanceActivity')
                    : translate('app.orderAdjustmentsReport.noBalanceActivity')
                }
                source="impactToBalance"
              />
              <OrderAdjustmentsReportStatusField
                label={translate('app.orderAdjustmentsReport.adjustmentStatus')}
              />
              <CopyToClipboardLinkField
                getLink={(record: RaRecord) =>
                  createPathWithQuery({
                    resource: Resources.Orders,
                    id: record?.order['@id'],
                    type: 'show',
                  })
                }
                getValue={getId}
                label="Kubera ID"
                sortable={false}
                source="order.@id"
                target="_blank"
              />
              <CopyToClipboardLinkField
                getLink={(record: RaRecord) =>
                  createPathWithQuery({
                    resource: Resources.Orders,
                    id: record?.order['@id'],
                    type: 'show',
                  })
                }
                isTrimValue
                label="Merchant ID"
                source="order.merchantReference"
                target="_blank"
              />
              <AdjustmentAmountField
                label={translate('app.orderAdjustmentsReport.amount')}
                source="amount"
              />
              <ChipField
                cellClassName="direction"
                headerClassName="direction"
                label={translate('app.orderAdjustmentsReport.orderDirection')}
                sortable={false}
                source="order.direction.description"
              />
              <AdjustmentChangeStatusField
                label={translate('app.orderAdjustmentsReport.orderStatusChange')}
                source="change-status"
              />
              <OrderAdjustmentsReportGatewayField
                label={translate('app.orderAdjustmentsReport.gateway')}
                onClick={gatewayModal.handleOpenRecordModal}
                source="order.gateway.name"
              />
              <TextField
                emptyText="—"
                label={translate('app.orderAdjustmentsReport.adjustmentDeclineReason')}
                source="rejectReason"
              />
              <CopyToClipboardField
                label={translate('app.orderAdjustmentsReport.client')}
                source="order.customer.customerId"
              />
              <EmbeddedFunctionField
                label={translate('app.orderAdjustmentsReport.author')}
                render={(record: User) => (
                  <RecordContextProvider value={record}>
                    <UserProfileField />
                  </RecordContextProvider>
                )}
                sortable={false}
                sourceField="user"
              />
              <DateTimeField
                label={translate('app.orderAdjustmentsReport.createdAt')}
                source="createdAt"
              />
              <DateTimeField
                label={translate('app.orderAdjustmentsReport.appliedAt')}
                source="updatedAt"
              />
              <ContextMenu
                render={(record: OrderAdjustments) => [
                  !!record?.comment && (
                    <ContextMenuCommentButton
                      key="comment-button"
                      onClick={() => commentDialog.handleOpenRecordModal(record)}
                    />
                  ),
                  <ContextMenuLinksButton
                    key="links-button"
                    onClick={() => confirmSearchModal.handleOpenRecordModal(record)}
                  />,
                ]}
              />
            </DatagridStyled>
          </DatagridStickyContainer>
          <GatewayShowDialog
            gateway={gatewayModal.record}
            isFetching={gatewayModal.isFetching}
            onClose={gatewayModal.handleCloseRecordModal}
            open={gatewayModal.openRecordModal}
          />
          <OrderAdjustmentsReportCommentDialog
            onClose={commentDialog.handleCloseRecordModal}
            open={commentDialog.openRecordModal}
            record={commentDialog.record}
          />
          <OrderAdjustmentsReportConfirmSearchDialog
            onClose={confirmSearchModal.handleCloseRecordModal}
            onConfirm={() => {
              if (!confirmSearchModal.record) {
                return;
              }
              onLinkClick(confirmSearchModal.record);
              confirmSearchModal.handleCloseRecordModal();
            }}
            open={confirmSearchModal.openRecordModal}
            record={confirmSearchModal.record}
          />
          <Pagination />
          <OrderAdjustmentsReportExportDialog
            closeNotifyDialog={onCloseNotifyDialog}
            exportType={exportType}
            isLoadingData={!!isFetching}
            listFilters={mapFormValuesToExportFilters(
              appliedFilters as OrderAdjustmentsReportListFilterFormValues,
            )}
            open={isOpenNotifyDialog}
          />
        </>
      )}
    </>
  );
};
